import React from 'react'
import Dialog from 'material-ui-legacy/Dialog'
import { Redirect } from 'react-router-dom'

import errors from '../../lib/errors.js'
import DeleteButton from '../UI/DeleteButton'
import EditButton from '../UI/EditButton'
import CloseButton from '../UI/CloseButton'
import LinearLoader from '../UI/LinearLoader'
import ErrorDialog from '../UI/ErrorDialog'
import Resource from '../../stores/resource'

/*
Props:
- resourceAPIEndpoint: The resource api endpoint (e.g. "agencies")
- resourceIncludes: resources that should be included with every resource fetch
                    (e.g.: ['agency', 'client', 'agencyProducer', 'user'] on Project)
- clientRootPath: client URL root path to actions like new & edit (e.g. '/agencies' -> '/agencies/new')
                  Edit/New actions will be disabled when this isn't present
- nameAttribute: The attribute (field) used to identify a row (example: name, fullName etc.)
- resourceSingularName: Singular name of resource (e.g. "agency")

- detailsCard: a *Card element to display item details

- editCallback,
  closeCallback,
  deleteCallback: Callback functions to enable buttons.
                  Each one receives the resource object.

- disableEdit, disableDelete: Boolean(false), disable buttons
*/
class ResourceDetails extends React.PureComponent {
  constructor(props) {
    super(props)

    const { resourceAPIEndpoint } = this.props

    this.resource = Resource(resourceAPIEndpoint)

    this.state = {
      resource: null,
      loading: true,
      error: null,
      redirect: null
    }
  }

  componentWillMount() {
    this._loadData()
  }

  _loadData = () => {
    const { resourceIncludes, match, resourceSingularName } = this.props
    const resourceId = match.params.id
    this.setState({ loading: true })
    this.resource
      .findBy({ id: resourceId }, resourceIncludes)
      .then(result => {

        // We don't get a 404 when using findBy
        if (!result.data) {
          this.setState({
            resource: null,
            loading: false,
            error: `Couldn't find ${resourceSingularName} with id ${resourceId}!`
          })
          return
        }

        this.setState({
          resource: result.data,
          loading: false,
          error: null
        })
      })
      .catch(error => {
        this.setState({
          resource: null,
          loading: false,
          error: errors.unpack(error)
        })
      })
  }

  _handleClose = () => {
    const { resource } = this.state
    const { closeCallback, clientRootPath } = this.props

    // Use closeCallback when present
    if (closeCallback) {
      closeCallback(resource)
    } else {
      this.setState({ redirect: clientRootPath })
    }
  }

  _handleEdit = () => {
    const { resource } = this.state
    const { editCallback, clientRootPath } = this.props

    // Use editCallback when present
    if (editCallback) {
      editCallback(resource)
      this._handleClose()
      return
    }

    // Redirect and close modal if no editCallback provided
    const redirect = `${clientRootPath}/${resource.id}/edit`
    this.setState({ redirect })
  }

  _handleDelete = () => {
    const { resource } = this.state
    const { deleteCallback } = this.props

    // Use deleteCallback when present
    if (deleteCallback) {
      deleteCallback(resource)
      this._handleClose()
      return
    }

    // Delete and close modal if no deleteCallback provided
    this.resource
      .destroy(resource.id)
      .then(() => this._handleClose)
      .catch(error => this.setState({ error: errors.unpack(error) }))
  }

  render() {
    const {
      disableEdit,
      disableDelete,
      nameAttribute,
      detailsCard
    } = this.props

    const { resource, loading, error, redirect } = this.state

    // Just skip everything if we're redirecting
    if (redirect) return <Redirect push to={redirect} />

    // Loading state
    if (loading)
      return (
        <Dialog modal={false} onRequestClose={this._handleClose} open>
          <LinearLoader />
        </Dialog>
      )

    // Error state
    if (error)
      return (
        <ErrorDialog open closeCallback={this._handleClose}>
          <p>
            <strong>Error:</strong> {error}
          </p>
        </ErrorDialog>
      )

    const resourceName = resource.attributes[nameAttribute || 'name']

    // Just so React doesn't complain about lowercase initials on components
    const Details = detailsCard

    // Action buttons
    let detailsActions = [
      <EditButton onTouchTap={this._handleEdit} disabled={disableEdit} />,
      <DeleteButton
        onTouchTap={this._handleDelete}
        disabled={disableDelete}
        confirmMessage={
          <p>
            Are you sure you want to delete <strong>{resourceName}</strong>?
            There's no undo!
          </p>
        }
      />,
      <CloseButton onTouchTap={this._handleClose} />
    ]

    return (
      <Dialog
        actions={detailsActions}
        modal={false}
        onRequestClose={this._handleClose}
        open
      >
        <Details itemData={resource} />
      </Dialog>
    )
  }
}

export default ResourceDetails



// WEBPACK FOOTER //
// ./src/components/UI/ResourceDetails.jsx